<template>
  <b-modal
    id="modal-cancelar-nfe"
    ref="modal"
    size="lg"
    hide-header
    hide-footer
    body-class="p-0 overflow-hidden"
    content-class="border-0 shadow-lg"
    no-close-on-backdrop
    @hidden="fechar"
    centered
  >
    <!-- Cabeçalho Premium -->
    <div class="modal-header-premium p-3 d-flex align-items-center bg-danger" 
         style="background: linear-gradient(135deg, #d9534f 0%, #c9302c 100%) !important;">
      <div class="header-icon-wrapper mr-3 bg-white-10">
        <i class="fa fa-ban text-white"></i>
      </div>
      <div>
        <h5 class="mb-0 font-weight-bold text-white">Cancelamento de NF-e</h5>
        <small class="text-white opacity-75">Informe o motivo para anular este documento</small>
      </div>
      <b-button variant="link" class="ml-auto text-white p-0 opacity-75" @click="fechar">
        <i class="fa fa-times fa-lg"></i>
      </b-button>
    </div>

    <div class="p-4" v-if="nfe">
      <!-- Barra de Informações -->
      <div class="modal-info-bar mb-4 bg-light border shadow-sm p-3 rounded">
        <b-row align-v="center">
          <b-col md="6">
            <small class="modal-label-premium">Destinatário</small>
            <h5 class="mb-0 font-weight-bold text-dark">{{ nfe.TX_NOME_CLIENTE || nfe.NOME_CLIENTE || 'Não informado' }}</h5>
          </b-col>
          <b-col md="3" class="border-left">
            <small class="modal-label-premium">NF-e / Série</small>
            <h5 class="mb-0 font-weight-bold text-primary">{{ nfe.TX_NUM_NFE || '---' }} / {{ nfe.ID_SERIE || '-' }}</h5>
          </b-col>
          <b-col md="3" class="border-left text-right">
            <small class="modal-label-premium">Valor Total</small>
            <h5 class="mb-0 font-weight-bold text-danger">{{ formatCurrency(nfe.VL_TOTAL_NFE) }}</h5>
          </b-col>
        </b-row>
      </div>

      <!-- Detalhes Adicionais -->
      <b-row class="mb-4">
        <b-col md="6">
          <b-form-group label="Protocolo de Autorização" label-class="modal-label-premium">
            <b-form-input :value="nfe.TX_NUM_PROTOCOLO" readonly class="rounded-pill bg-light border-0 shadow-xs" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Chave de Acesso" label-class="modal-label-premium">
            <b-form-input :value="nfe.TX_CHAVE_ACESSO" readonly class="rounded-pill bg-light border-0 shadow-xs" />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Justificativa -->
      <div class="modal-section-title mb-3">
        <i class="fa fa-commenting-o mr-2 text-danger"></i> Justificativa do Cancelamento
      </div>
      
      <b-form-group description="Mínimo 15 e máximo 255 caracteres">
        <b-form-textarea
          v-model="justificativa"
          rows="4"
          max-rows="8"
          placeholder="Descreva detalhadamente o motivo do cancelamento..."
          class="rounded-lg shadow-xs border-0 bg-light p-3"
          :state="justificativaValida"
        />
        <b-form-invalid-feedback :state="justificativaValida">
          A justificativa deve ter entre 15 e 255 caracteres.
        </b-form-invalid-feedback>
        <div class="d-flex justify-content-end mt-1">
          <small class="text-muted font-weight-bold">{{ justificativa.length }} / 255</small>
        </div>
      </b-form-group>

      <!-- Alert informativo -->
      <b-alert show variant="warning" class="rounded-lg border-0 shadow-sm mb-0 mt-3">
        <div class="d-flex align-items-center">
          <i class="fa fa-exclamation-triangle mr-3 fa-lg"></i>
          <div>
            <strong class="d-block">Atenção: Operação Irreversível</strong>
            <span class="small">O cancelamento da NF-e não pode ser desfeito após a transmissão para a SEFAZ.</span>
          </div>
        </div>
      </b-alert>
    </div>

    <!-- Rodapé Premium -->
    <div class="modal-footer-premium p-3 bg-light d-flex justify-content-end border-top">
      <b-button variant="light" @click="fechar" class="mr-2 rounded-pill px-4 shadow-sm font-weight-bold">
        <i class="fa fa-times mr-1"></i> Cancelar
      </b-button>
      <b-button 
        variant="danger" 
        @click="confirmarCancelamento"
        :disabled="!justificativaValida || processando"
        class="rounded-pill px-4 shadow-sm font-weight-bold"
      >
        <b-spinner small v-if="processando" class="mr-1" />
        <i class="fa fa-ban mr-1" v-else></i> Confirmar Cancelamento
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import Tools from '@/tools/tools.js'

export default {
  name: 'CancelarNfeModal',
  
  data() {
    return {
      nfe: null,
      justificativa: '',
      processando: false
    }
  },

  computed: {
    justificativaValida() {
      if (!this.justificativa || this.justificativa.trim().length === 0) {
        return null
      }
      const len = this.justificativa.trim().length
      return len >= 15 && len <= 255
    }
  },

  methods: {
    abrir(nfe) {
      this.nfe = nfe
      this.justificativa = ''
      this.processando = false
      this.$refs.modal.show()
    },

    fechar() {
      this.$refs.modal.hide()
      this.nfe = null
      this.justificativa = ''
      this.processando = false
    },

    async confirmarCancelamento() {
      // Validação final
      if (!this.justificativaValida) {
        Tools.exibirMensagem(
          this.$store,
          'Atenção',
          'warning',
          'A justificativa deve ter entre 15 e 255 caracteres'
        )
        return
      }

      // Solicitar confirmação do usuário
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Confirma o cancelamento da NF-e ${this.nfe.TX_NUM_NFE || this.nfe.ID}?\n\nEsta operação é irreversível!`,
        {
          title: 'Confirmar Cancelamento',
          size: 'md',
          buttonSize: 'md',
          okVariant: 'danger',
          okTitle: 'Sim, Cancelar',
          cancelTitle: 'Não',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        }
      )

      if (!confirmed) {
        return
      }

      // Executar cancelamento
      await this.cancelarNfe()
    },

    async cancelarNfe() {
      this.processando = true
      
      try {
        const payload = {
          nfeId: this.nfe.ID,
          justificativa: this.justificativa.trim(),
          usuario: window.localStorage.getItem('login') || 'sistema'
        }

        const response = await this.$http.post('/api/protected/notaFiscal/cancelarIntegrador/', payload)
        
        const resultado = response.data

        if (resultado && resultado.sucesso) {
          Tools.exibirMensagem(this.$store, 'Sucesso', 'success', 'NF-e cancelada com sucesso')
          
          // Abrir PDF do cancelamento se disponível
          if (resultado.pdfBase64) {
            try {
              // Converte base64 para blob
              const byteCharacters = atob(resultado.pdfBase64)
              const byteNumbers = new Array(byteCharacters.length)
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i)
              }
              const byteArray = new Uint8Array(byteNumbers)
              const blob = new Blob([byteArray], { type: 'application/pdf' })

              // Cria a URL e abre o PDF
              const url = window.URL.createObjectURL(blob)
              window.open(url)
            } catch (errPdf) {
              console.error('Erro ao abrir PDF do Cancelamento:', errPdf)
            }
          }
          
          this.$emit('nfe-cancelada', { nfeId: this.nfe.ID, resultado })
          this.fechar()
        } else {
          const erro = resultado?.erro || resultado?.mensagem || 'Erro ao cancelar NF-e'
          Tools.exibirMensagem(this.$store, 'Erro', 'danger', erro)
        }

      } catch (err) {
        console.error('Erro ao cancelar NF-e:', err)
        Tools.exibirMensagem(this.$store, 'Erro', 'danger', err)
      } finally {
        this.processando = false
      }
    },

    formatCurrency(val) {
      return Tools.formatCurrency(val)
    }
  }
}
</script>

<style scoped>
.small {
  font-size: 0.875rem;
}
</style>



// WEBPACK FOOTER //
// src/views/faturamento/nfe/partials/CancelarNfeModal.vue